import { ControlsCommonProps } from "components/Form/fields"
import { ClipSpinner } from "components/Spinner"
import { ArrowDownIcon } from "components/icons/icons"
import styles from './autoComplete.module.scss'
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Input } from "../Input/Input"
import { Option } from "./Option"
import { emptyArray, EntityPrefixEnum, getFormatedId } from "utils/commonHelper"
import Spinner from "components/Spinner/Spinner"
import { GenericFilterModel, GenericFilterModelCollection, IdNameResponse, IdNameResponseItemsResponseModel, IdNameResponseSimpleResponseModel } from 'services/tenantManagementService'
import { tryCatchJsonByAction } from 'utils/fetchUtils'
import { Translation } from 'components/Translations/Translation'
import { PortalToDropdown } from "components/Portal/PortalToDropdown"
import { autoCompleteId } from "./AutoComplete"
import { emptyItemsModel, ITEMS_LIMIT, ItemsModel, SEARCH_DEBOUNCE } from "./remoteHelper"

export type AutoCompleteRemoteProps = ControlsCommonProps<number | undefined> & {
	entityPrefix?: EntityPrefixEnum
	filters?: GenericFilterModel[]
	fetchItemsFunction(genericFilter: GenericFilterModelCollection, searchValue: string | undefined): Promise<IdNameResponseItemsResponseModel>
	fetchSingleItemFunction(id: number): Promise<IdNameResponseSimpleResponseModel>
	getItemText?(item: IdNameResponse): string | undefined
	getItemStyle?: (item: IdNameResponse) => React.CSSProperties
	/** If there is a need for different display in Input and Option */
	getText?(item: IdNameResponse): string | undefined
	placeholder?: string
	focus?: boolean
	size?: 'medium' | 'small'
}

export const AutoCompleteRemote = (props: AutoCompleteRemoteProps) => {
	const {
		value, onChange, onBlur, disabled,
		entityPrefix,
		filters, fetchItemsFunction, fetchSingleItemFunction,
		getItemText, getItemStyle, getText,
		placeholder, size = 'medium',
		focus = false
	} = props;

	const containerRef = useRef<HTMLDivElement>(null);

	const [expanded, setExpanded] = useState(false);
	const [searchValue, setSearchValue] = useState<string>();
	const [isFetchingSearchItems, setIsFetchingSearchItems] = useState<boolean>(false);
	const [filteredItems, setFilteredItems] = useState<ItemsModel>(emptyItemsModel);

	const [offset, setOffset] = useState(0);
	const [keyboardItem, setKeyboardItem] = useState<any | undefined>();

	const [selectedItem, setSelectedItem] = useState<IdNameResponse | undefined>(undefined)
	const [isFetchingValue, setIsFetchingValue] = useState<boolean>(false);

	const lastItemRef = useRef(null);

	useEffect(
		() => {
			// if we fetched all items, or fetching is in progress, skip
			if (filteredItems.items.length === filteredItems.count || isFetchingSearchItems) {
				return;
			}

			const callback = (entries) => {
				// if scrolled to the last item, update offset and trigger new fetch
				if (entries[0].isIntersecting && filteredItems.count && offset + ITEMS_LIMIT < filteredItems.count) {
					setOffset((prev) => prev + ITEMS_LIMIT);
				}
			};

			const observerInstance = new IntersectionObserver(callback, {
				root: null,
				threshold: 1.0,
			});

			if (lastItemRef.current) {
				observerInstance.observe(lastItemRef.current);
			}

			return () => {
				if (lastItemRef.current) {
					// eslint-disable-next-line react-hooks/exhaustive-deps
					observerInstance.unobserve(lastItemRef.current);
				}
			}
		},
		[filteredItems, isFetchingSearchItems, offset]
	);

	useEffect(
		() => {
			if (focus) {
				setExpanded(true);
			}
		},
		[focus]
	)

	const fetchFilteredItemsCallback = useCallback(
		async () => {
			const bindedAction = fetchItemsFunction.bind(null, new GenericFilterModelCollection({filters, offset: offset, limit: ITEMS_LIMIT}), searchValue);
			const response = await tryCatchJsonByAction(bindedAction);
			if (response.success) {
				let items = response.items || emptyArray;

				setFilteredItems((state => {
					return {
						items: offset > 0 ? [...state.items, ...items] : items,
						count: response.count
					}
				}))
			}
			setIsFetchingSearchItems(false)
		},
		[searchValue, filters, fetchItemsFunction, offset]
	)

	useEffect(
		() => {
			// set fetching, while user is typing
			setIsFetchingSearchItems(true)

			// if autocomplete is just focused or user is scrolling, fetch immediately
			if (!searchValue || offset > 0) {
				expanded && fetchFilteredItemsCallback()
			} else {
				const handler = setTimeout(() => {
					expanded && fetchFilteredItemsCallback()
				}, SEARCH_DEBOUNCE);

				return () => {
					clearTimeout(handler);
				}
			}
		},
		[fetchFilteredItemsCallback, expanded, searchValue, offset]
	)

	const fetchSelectedItemDataCallback = useCallback(
		async (id: number) => {
			setIsFetchingValue(true)

			const bindedAction = fetchSingleItemFunction.bind(null, id);
			const response = await tryCatchJsonByAction(bindedAction);
			if (response.success) {
				setSelectedItem(response.value)
			}
			setIsFetchingValue(false)
		},
		[fetchSingleItemFunction]
	)

	useEffect(
		() => {
			if (value) {
				value !== selectedItem?.id && fetchSelectedItemDataCallback(Number(value))
			} else {
				setSelectedItem(undefined)
			}
		},
		[fetchSelectedItemDataCallback, value, selectedItem]
	)

	const onChangeCallback = useCallback(
		(item: IdNameResponse | undefined) => {
			let id: number | undefined = item?.id;

			if (id !== value) {
				onChange && onChange(id);
			}
			setSelectedItem(item)
			setSearchValue(undefined);
			setFilteredItems(emptyItemsModel)
			setOffset(0);
			setExpanded(false);
			setKeyboardItem(undefined);
		},
		[onChange, value]
	)

	const onBlurCallback = useCallback(
		() => {
			if (searchValue === '') {
				onChangeCallback(undefined)
			}
			onBlur && onBlur();
			setSearchValue(undefined);
			setFilteredItems(emptyItemsModel)
			setOffset(0);
			setExpanded(false);
			setKeyboardItem(undefined);
		},
		[onBlur, onChangeCallback, searchValue]
	)

	const expandCallback = useCallback(
		() => !disabled && setExpanded(true),
		[disabled]
	)

	const onKeyDownCallback = useCallback(
		(eventKey: string) => {
			if (!expanded) {
				return;
			}

			switch (eventKey) {
				case 'Enter':
					if (keyboardItem) {
						onChangeCallback(keyboardItem);
					} else if (searchValue === '') {
						onChangeCallback(undefined);
					}
					break;
				case 'ArrowUp':
					setKeyboardItem((state) => {
						const index = filteredItems.items.indexOf(state);

						if (index === -1) {
							return filteredItems.items.at(-1);
						} else {
							return filteredItems.items.at(index - 1);
						}
					})
					break;
				case 'ArrowDown':
					setKeyboardItem((state) => {
						const index = filteredItems.items.indexOf(state);

						if (index === filteredItems.items.length - 1) {
							return filteredItems.items.at(0);
						} else {
							return filteredItems.items.at(index + 1);
						}
					})
					break;
			}
		},
		[searchValue, filteredItems, keyboardItem, onChangeCallback, expanded]
	)

	const getItemTextMemo = useMemo(
		() => {
			// if getItemText is provided use it
			if (getItemText) {
				return getItemText;
			}

			// if entity prefix is provided, create default getItemText
			if (entityPrefix) {
				return (item: IdNameResponse) => `${getFormatedId(entityPrefix, item.id)} - ${item.name}`;
			}

			// otherwise, return just name
			return (item: IdNameResponse) => item.name;
		},
		[entityPrefix, getItemText]
	)

	const optionsContent = useMemo(
		() => {
			return filteredItems.items.map((item, index) => {
				const id = item.id;

				return (
					<Option
						key={item.id}
						item={item}
						getItemText={getItemTextMemo}
						onClick={onChangeCallback}
						isSelected={value === id || keyboardItem === item}
						getItemStyle={getItemStyle}
						ref={index === filteredItems.items.length - 1 ? lastItemRef : undefined}
					/>
				)
			})
		},
		[filteredItems, onChangeCallback, getItemStyle, value, keyboardItem, getItemTextMemo]
	)

	const selectedItemStyle = useMemo(
		() => {
			if (selectedItem) {
				return getItemStyle?.(selectedItem);
			}
		},
		[getItemStyle, selectedItem]
	)

	const selectedItemText = selectedItem ? (getText?.(selectedItem) || getItemTextMemo(selectedItem)) : undefined;

	const dropdownContentMemo = useMemo(
		() => (
			<div className={styles.dropdown}>
				{optionsContent}
				{/* loading */}
				{isFetchingSearchItems &&
					<div className={styles.option_spinner}>
						<Spinner>
							<ClipSpinner size={20} />
						</Spinner>
					</div>
				}
				{optionsContent.length === 0 && !isFetchingSearchItems && (
					<div className={styles.no_options}>
						<Translation i18n='i18n.label.noOptions' />
					</div>
				)}
			</div>
		),
		[optionsContent, isFetchingSearchItems]
	)

	const searchValueChangeCallback = useCallback(
		(value: string | undefined) => {
			setSearchValue(value);
			setFilteredItems(emptyItemsModel);
			setOffset(0);
		},
		[]
	)

	return (
		<div ref={containerRef} id={autoCompleteId} className={`${styles.container} ${size === 'small' ? styles.small : ''}`}>
			<div className={styles.select_container} onClick={expandCallback}>
				<Input
					value={searchValue !== undefined ? searchValue : selectedItemText}
					onChange={searchValueChangeCallback}
					onBlur={onBlurCallback}
					onFocus={expandCallback}
					onKeyDown={onKeyDownCallback}
					placeholder={placeholder}
					disabled={disabled}
					hideMaxLength
					selectAllTextOnFocus
					size={size}
					focus={focus}
					style={selectedItemStyle}
				/>
				{/* dropdown */}
				{expanded &&
					<PortalToDropdown containerRef={containerRef}>
						{dropdownContentMemo}
					</PortalToDropdown>
				}
				{/* arrow */}
				<div className={`${styles.arrow} ${size === 'small' ? styles.small : ''} ${disabled ? styles.arrow_disabled : ''}`}>
					<ArrowDownIcon width={8} height={8} fill='currentColor' />
				</div>
				{/* loading */}
				{isFetchingValue &&
					<div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}>
						<Spinner>
							<ClipSpinner size={20} />
						</Spinner>
					</div>
				}
			</div>
		</div>
	)
}
